import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as CropAidSelectors from 'src/app/crop-aid/store/crop-aid.selectors';
import {
  Fertilizer,
  FertilizerDataByType,
  FertilizerType,
} from 'src/app/fertilizers/models/fertilizer.model';
import { selectManureSpaceDetailsResource } from 'src/app/manure-space/store/manure-space.selectors';
import { ReportManureSubstancesView } from 'src/app/reports/models';
import { ReportSortService } from 'src/app/reports/services/report-sort.service';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import * as SoilTypeSelectors from 'src/app/soil-type/store/soil-type.selectors';
import { getCropYear, getReportManureSubstancesView, getReportSort } from 'src/app/store/selectors';

import { CropFieldLegalStandardData } from '../fertilization-legal-standards.models';
import * as LegalStandardSelectors from '../store/fertilization-legal-standard.selectors';

const MAX_LENGTH_MANURECODE_WITH_PREFIX = 3;

@Component({
  selector: 'app-fertilization-legal-standards',
  templateUrl: './fertilization-legal-standards.component.html',
  styleUrls: ['./fertilization-legal-standards.component.scss'],
  standalone: false,
})
export class FertilizationLegalStandardsComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  private readonly window = inject<WindowService>(WindowServiceInjector);
  private readonly router = inject(Router);

  private readonly reportTitle = this.locale.translate('legalStandardsReport');
  private readonly destroyed$ = new Subject<boolean>();
  private readonly cropAids$ = this.store.select(CropAidSelectors.selectCropAidFertilizers);
  private readonly soilTypes$ = this.store.select(SoilTypeSelectors.selectSoilTypes);
  private readonly salesOrderPeriods$ = this.store.select(
    SalesOrderPeriodSelectors.selectSalesOrderPeriods,
  );

  customer$ = this.store.select(UserSelectors.selectCustomer);
  reportSort$ = this.store.select(getReportSort);
  reportManureSubstancesView$ = this.store.select(getReportManureSubstancesView);

  mainCropFields$ = this.sort(this.store.select(LegalStandardSelectors.selectMainCropFields));
  subsequentCultivations$ = this.sort(
    this.store.select(LegalStandardSelectors.selectSubsequentCultivations),
  );
  legalStandardBalance$ = this.store.select(LegalStandardSelectors.selectLegalStandardBalance);
  pageTitle$ = this.store.select(
    LegalStandardSelectors.selectFertilizationLegalStandardsReportTitle,
    {
      reportTitle: this.reportTitle,
    },
  );
  loggedInEmployee$ = this.store.select(LegalStandardSelectors.selectLoggedInEmployee);
  private readonly manureSpaceDetailsResource$ = this.store.select(
    selectManureSpaceDetailsResource,
  );
  fertilizers$ = combineLatest([
    this.manureSpaceDetailsResource$,
    this.reportManureSubstancesView$,
  ]).pipe(
    map(([resource, reportManureSubstancesView]) =>
      resource?.state === 'loaded'
        ? this.getReportFertilizers(resource.data?.fertilizers ?? [], reportManureSubstancesView)
        : [],
    ),
  );
  manureSpaceComment$ = this.manureSpaceDetailsResource$.pipe(
    map((resource) => (resource?.state === 'loaded' ? (resource.data?.comment ?? '') : '')),
  );

  fertilizerData$ = combineLatest([
    this.fertilizers$,
    this.reportManureSubstancesView$,
    this.cropAids$,
    this.soilTypes$,
    this.salesOrderPeriods$,
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([fertilizers, reportManureSubstancesView, cropAids, soilTypes, salesOrderPeriods]) =>
      fertilizers.map((fertilizer) => ({
        ...fertilizer,
        cropAidName: this.getCropAidName(
          fertilizer,
          reportManureSubstancesView,
          cropAids.find((cropAid) => cropAid.id === fertilizer.cropAidId)?.name ?? '',
        ),
        cropAidFertilizerType: cropAids.find((cropAid) => cropAid.id === fertilizer.cropAidId)
          ?.fertilizer.fertilizerType,
        soilTypeName: fertilizer.soilTypeId
          ? soilTypes.find((soilType) => soilType.id === fertilizer.soilTypeId)?.name
          : '',
        salesOrderPeriodName:
          fertilizer.periodId &&
          fertilizer.fertilizerType !== FertilizerType.InitialStock &&
          fertilizer.fertilizerType !== FertilizerType.ClosingStock
            ? salesOrderPeriods.find(
                (salesOrderPeriod) => salesOrderPeriod.id === fertilizer.periodId,
              )?.name
            : '',
      })),
    ),
  );
  fertilizerDataByType: FertilizerDataByType[] = [];

  buttonItems$ = this.sortService.getButtonItems();
  selectItems$ = this.sortService.getSelectItems();

  cropYear$ = this.store.select(getCropYear);

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.fertilizerData$.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      const newFertilizerDataByType: FertilizerDataByType[] = [];
      Object.values(FertilizerType).forEach((fertilizerType) => {
        const fertilizerData = data.filter(
          (itemData) => itemData?.fertilizerType === fertilizerType,
        );
        if (fertilizerData.length) {
          newFertilizerDataByType.push({ fertilizerType, fertilizerData });
        }
      });
      this.fertilizerDataByType = newFertilizerDataByType;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  print() {
    this.window.print();
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }

  selectAction(data: [string, ReportManureSubstancesView]) {
    this.sortService.handleSelectAction(data);
  }

  private getReportFertilizers(
    fertilizers: Fertilizer[],
    reportManureSubstancesView: ReportManureSubstancesView,
  ): Fertilizer[] {
    switch (reportManureSubstancesView) {
      case ReportManureSubstancesView.manureType:
        return this.groupFertilizers(fertilizers, (fertilizer) =>
          [
            fertilizer.cropAidId,
            fertilizer.manureCode,
            fertilizer.soilTypeId,
            fertilizer.fertilizerType,
          ].join('|'),
        ).map((fertilizer) => ({ ...fertilizer, periodId: 0 }));
      case ReportManureSubstancesView.period:
        return this.groupFertilizers(fertilizers, (fertilizer) =>
          [
            fertilizer.cropAidId,
            fertilizer.manureCode,
            fertilizer.soilTypeId,
            fertilizer.periodId,
            fertilizer.fertilizerType,
          ].join('|'),
        );
      case ReportManureSubstancesView.manureDetails:
        return fertilizers;
    }
    return fertilizers;
  }

  private groupFertilizers(
    fertilizers: Fertilizer[],
    getGroupKey: (fertilizer: Fertilizer) => string,
  ): Fertilizer[] {
    const fertilizerGroups = new Map<string, Fertilizer[]>();
    fertilizers.forEach((fertilizer) => {
      const key = getGroupKey(fertilizer);
      fertilizerGroups.set(key, [...(fertilizerGroups.get(key) ?? []), fertilizer]);
    });

    return Array.from(fertilizerGroups.values()).map((group) => {
      const [first] = group;
      return {
        ...first,
        dosage: group.reduce((total, fertilizer) => total + fertilizer.dosage, 0),
        totalNitrogen: group.reduce((total, fertilizer) => total + fertilizer.totalNitrogen, 0),
        totalPhosphate: group.reduce((total, fertilizer) => total + fertilizer.totalPhosphate, 0),
        comment: group.every((fertilizer) => fertilizer.comment === first.comment)
          ? first.comment
          : '',
      };
    });
  }

  private getCropAidName(
    fertilizer: Fertilizer,
    reportManureSubstancesView: ReportManureSubstancesView,
    cropAidName: string,
  ): string {
    if (
      reportManureSubstancesView === ReportManureSubstancesView.manureDetails ||
      fertilizer.manureCode === null ||
      fertilizer.manureCode === undefined
    ) {
      return cropAidName;
    }

    const formattedManureCode = String(fertilizer.manureCode).padStart(
      MAX_LENGTH_MANURECODE_WITH_PREFIX,
      '0',
    );
    return `${formattedManureCode} ${cropAidName}`;
  }

  private sort(
    cropFields$: Observable<CropFieldLegalStandardData[]>,
  ): Observable<CropFieldLegalStandardData[]> {
    return combineLatest([cropFields$, this.reportSort$]).pipe(
      map(([cropFields, reportSort]) =>
        cropFields && reportSort
          ? this.sortService.sort({
              elements: cropFields,
              option: reportSort.option,
              descending: reportSort.descending,
              cropFieldNameFunc: (f) => f.cropFieldName,
              cropNameFunc: (f) => f.cropName,
            })
          : [],
      ),
    );
  }
}
